import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Option,
  OptionOnSelectData,
  SelectionEvents,
  Spinner,
  Text,
  Textarea
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation, Trans } from 'react-i18next';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import * as ComponentFunctions from './ConceptValuesForm.Functions';
import { useConceptValuesFormStyles } from './ConceptValuesForm.Styles';

const ConceptValuesFormComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useConceptValuesFormStyles();

  const [values, setValues] = useState<ComponentFunctions.FormValues>(
    ComponentFunctions.initFormValues(props.concept)
  );
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState<ComponentFunctions.FormValidation | undefined>();
  const [pendingSponsorId, setPendingSponsorId] = useState<string | undefined>();
  const [pendingConceptName, setPendingConceptName] = useState<string | undefined>();

  // Reinicializar al abrir / cambiar concepto
  useEffect(() => {
    if (props.isOpen) {
      setValues(ComponentFunctions.initFormValues(props.concept));
      setSubmitted(false);
      setValidation(undefined);
      if (!props.isLoadingCategories && props.categories.length === 0) {
        props.GetAllConceptCategories();
      }
    }
  }, [props.isOpen, props.concept?.id]);

  // Cerrar tras actualizar exitosamente
  useEffect(() => {
    if (submitted && props.successUpdating) {
      props.onSuccess?.();
      props.dismissPanel();
    }
  }, [props.successUpdating]);

  // Cerrar (y asociar al sponsor) tras crear exitosamente
  useEffect(() => {
    if (!submitted || !props.successAdding) return;
    if (pendingSponsorId && pendingConceptName) {
      const newConcept = props.concepts.find((c) => c.conceptName === pendingConceptName);
      if (newConcept) {
        props.AddConceptSponsor({ conceptId: newConcept.id, sponsorId: pendingSponsorId });
      }
      setPendingSponsorId(undefined);
      setPendingConceptName(undefined);
    }
    props.onSuccess?.();
    props.dismissPanel();
  }, [props.successAdding]);

  const set = (field: keyof ComponentFunctions.FormValues) =>
    (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValues((prev) => ({ ...prev, [field]: ev.target.value }));

  const handleSave = () => {
    setSubmitted(true);
    const v = ComponentFunctions.validateForm(values, t);
    setValidation(v);
    if (!v.success) return;
    if (props.concept) {
      props.UpdateConcept(ComponentFunctions.buildUpdatedConcept(props.concept, values));
    } else {
      setPendingConceptName(values.conceptName.trim());
      if (props.sponsorId) {
        setPendingSponsorId(props.sponsorId);
      }
      props.AddConcept(ComponentFunctions.buildNewConcept(values));
    }
  };

  const handleDismiss = () => {
    setSubmitted(false);
    setValidation(undefined);
    props.dismissPanel();
  };

  return (
    <Drawer
      open={props.isOpen}
      onOpenChange={(_, { open }) => !open && handleDismiss()}
      position="end"
      size="medium"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={handleDismiss}
            />
          }
        >
          {props.concept
            ? t('conceptValuesForm.title', 'Editar Concepto')
            : t('conceptValuesForm.titleNew', 'Nuevo Concepto')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.body}>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t('conceptValuesForm.reviewInformation', 'Por favor revise los siguientes inconvenientes:')}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {props.errorMessage && (
          <MessageBar intent="error">
            <MessageBarBody>{props.errorMessage}</MessageBarBody>
          </MessageBar>
        )}

        {/* Información general */}
        <Text className={styles.sectionTitle}>
          {t('conceptValuesForm.section.general', 'Información general')}
        </Text>

        <Field
          label={t('conceptValuesForm.fields.name', 'Nombre')}
          required
          validationMessage={
            submitted && !values.conceptName.trim()
              ? t('conceptValuesForm.validation.nameRequired', 'El nombre es obligatorio')
              : undefined
          }
        >
          <Input
            value={values.conceptName}
            onChange={set('conceptName')}
            placeholder={t('conceptValuesForm.fields.namePlaceholder', 'Ingrese el nombre del concepto')}
          />
        </Field>

        <Field label={t('conceptValuesForm.fields.description', 'Descripción')}>
          <Textarea
            value={values.conceptDescription}
            onChange={set('conceptDescription')}
            placeholder={t('conceptValuesForm.fields.descriptionPlaceholder', 'Ingrese la descripción')}
            rows={3}
          />
        </Field>

        <Field label={t('conceptValuesForm.fields.code', 'Código')}>
          <Input
            value={values.conceptCode}
            onChange={set('conceptCode')}
            placeholder={t('conceptValuesForm.fields.codePlaceholder', 'Ingrese el código del concepto')}
          />
        </Field>

        <Field label={t('conceptValuesForm.fields.category', 'Categoría')}>
          {props.isLoadingCategories ? (
            <Spinner size="tiny" />
          ) : (
            <Dropdown
              placeholder={t('conceptValuesForm.fields.categoryPlaceholder', 'Seleccione una categoría')}
              value={
                props.categories.find((c) => c.id === values.conceptCategoryId)
                  ?.conceptCategoryName ?? ''
              }
              selectedOptions={
                values.conceptCategoryId != null ? [String(values.conceptCategoryId)] : []
              }
              onOptionSelect={(_: SelectionEvents, data: OptionOnSelectData) => {
                const id = data.optionValue ? parseInt(data.optionValue, 10) : undefined;
                setValues((prev) => ({ ...prev, conceptCategoryId: id }));
              }}
            >
              <Option value="" text="">
                {t('conceptValuesForm.fields.categoryNone', '— Sin categoría —')}
              </Option>
              {props.categories.map((cat) => (
                <Option key={cat.id} value={String(cat.id)} text={cat.conceptCategoryName ?? ''}>
                  {cat.conceptCategoryName}
                </Option>
              ))}
            </Dropdown>
          )}
        </Field>

        <Divider />

        {/* Precios */}
        <Text className={styles.sectionTitle}>
          {t('conceptValuesForm.section.pricing', 'Precios')}
        </Text>

        <CurrencySelectorComponent
          label={t('conceptValuesForm.fields.currency', 'Moneda')}
          defaultSelected={values.costCurrency}
          errorMessage={undefined}
          onCurrencyChange={(currency) =>
            setValues((prev) => ({ ...prev, costCurrency: currency }))
          }
        />

        <div className={styles.costsGrid}>
          <Field label={t('conceptValuesForm.fields.referenceCost', 'Costo de referencia')}>
            <Input
              type="number"
              value={values.referenceCost}
              onChange={set('referenceCost')}
              placeholder="0.00"
              contentAfter={values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined}
            />
          </Field>

          <Field label={t('conceptValuesForm.fields.sponsorCost', 'Costo Sponsor')}>
            <Input
              type="number"
              value={values.sponsorWeigthedCost}
              onChange={set('sponsorWeigthedCost')}
              placeholder="0.00"
              contentAfter={values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined}
            />
          </Field>

          <Field label={t('conceptValuesForm.fields.siteCost', 'Costo Sitio')}>
            <Input
              type="number"
              value={values.siteWeigthedCost}
              onChange={set('siteWeigthedCost')}
              placeholder="0.00"
              contentAfter={values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined}
            />
          </Field>

          <Field label={t('conceptValuesForm.fields.countryCost', 'Costo País')}>
            <Input
              type="number"
              value={values.countryWeigthedCost}
              onChange={set('countryWeigthedCost')}
              placeholder="0.00"
              contentAfter={values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined}
            />
          </Field>

          <Field label={t('conceptValuesForm.fields.globalCost', 'Costo Global')}>
            <Input
              type="number"
              value={values.globalWeigthedCost}
              onChange={set('globalWeigthedCost')}
              placeholder="0.00"
              contentAfter={values.costCurrency ? <Text size={200}>{values.costCurrency}</Text> : undefined}
            />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <Button onClick={handleDismiss}>
          <Trans i18nKey="conceptValuesForm.cancelButton" defaults="Cancelar" />
        </Button>
        <Button
          appearance="primary"
          onClick={handleSave}
          disabled={props.isUpdating || props.isAdding}
        >
          {(props.isUpdating || props.isAdding) ? (
            <Spinner size="tiny" />
          ) : (
            <Trans i18nKey="conceptValuesForm.saveButton" defaults="Guardar" />
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

const ConnectedComponent = connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ConceptValuesFormComponent as any);

export default ConnectedComponent as React.ComponentType<ComponentFunctions.ConceptValuesFormOwnProps>;
